import ClaimsService from "@/api/services/claims";
import { toCamelCase, toSnakeCase, isPresent } from "@estaly/ui";

const STATUS_CLASS_MAP = {
  approved: "bg-green-200 text-green-800",
  to_be_approved: "bg-yellow-200 text-yellow-800",
  refused: "bg-red-200 text-red-800",
};
const DEFAULT_STATUS_CLASS = "bg-gray-200 text-gray-600";

const initialState = {
  currentClaim: null,
  index: [],
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  pendingCount: 0,
};

export default {
  namespaced: true,

  state: initialState,

  getters: {
    isActiveClaim: () => (claim) => {
      const closedStatuses = ["resolved", "cancelled", "refused"];
      return !closedStatuses.includes(claim?.status?.toLowerCase());
    },

    getClaimFriendlyId: (state) => {
      return isPresent(state.currentClaim) ? state.currentClaim.friendlyId : null;
    },
    doesClaimExists: (state) => {
      return isPresent(state.currentClaim);
    },
    answers: (state) => {
      return isPresent(state.currentClaim) ? JSON.parse(JSON.stringify(state.currentClaim.answers)) : null;
    },
    claimWaitingForQuotation: (state) => {
      return isPresent(state.currentClaim) ? state.currentClaim?.status === "waiting_for_quotation" : null;
    },
    claimWaitingForInvoice: (state) => {
      return isPresent(state.currentClaim) ? state.currentClaim?.status === "waiting_for_invoice" : null;
    },
    anyRequiredAction: (state, getters) => {
      return getters.merchantQuotationRequired || getters.merchantInvoiceRequired;
    },
    claimIsHandledByMerchant: (state) => {
      return isPresent(state.currentClaim) ? state.currentClaim?.claimHandler === "merchant" : null;
    },
    hasRequiredAction: () => (claim) => {
      const status = claim.status?.value || claim.status; // index (TO DO) vs show
      const claimHandler = claim.claimHandler;
      return claimHandler === "merchant" && (status === "waiting_for_quotation" || status === "waiting_for_invoice");
    },
    merchantQuotationRequired: (state, getters) => {
      return getters.claimIsHandledByMerchant && getters.claimWaitingForQuotation;
    },
    merchantInvoiceRequired: (state, getters) => {
      return getters.claimIsHandledByMerchant && getters.claimWaitingForInvoice;
    },
    claimIsHandledByExternalMerchant: (state) => {
      return isPresent(state.currentClaim) ? state.currentClaim?.claimHandler === "external_merchant" : null;
    },
    customerQuotationExpected: (state, getters) => {
      return getters.claimIsHandledByExternalMerchant && getters.claimWaitingForQuotation;
    },
    customerInvoiceExpected: (state, getters) => {
      return getters.claimIsHandledByExternalMerchant && getters.claimWaitingForInvoice;
    },
    claimPickupScheduled: (state) => {
      return state.currentClaim?.status === "pickup_scheduled";
    },

    replacementHandlingType: (state) => {
      return state.currentClaim?.handlingType === "replacement";
    },
    statusStyle: () => (status) => {
      switch (status) {
        // Statuts rouges (refus et annulations)
        case "cancelled":
        case "refused":
        case "quotation_refused":
        case "invoice_refused":
          return "bg-red-100 text-red-800";

        // Statuts orange (en attente d'approbation)
        case "to_be_approved":
        case "quotation_to_be_approved":
        case "invoice_to_be_approved":
          return "bg-indigo-100 text-indigo-800";

        // Statuts bleus (en cours de traitement/attente)
        case "processing":
        case "pending_deductible_payment":
        case "pending_deductible_payment_and_final_settlement":
        case "pending_final_settlement":
        case "waiting_for_quotation":
        case "waiting_for_customer_quotation":
        case "waiting_for_invoice":
        case "waiting_for_clients_bank_details":
        case "waiting_for_resolution":
        case "waiting_for_customer_iban":
        case "waiting_for_return_appointment":
        case "pickup_scheduled":
          return "bg-blue-100 text-blue-800";

        // Statuts verts (approuvés/résolus)
        case "resolved":
        case "approved":
        case "clients_bank_details_uploaded":
        case "voucher_sent":
        case "pickup_completed":
        case "quotation_approved":
        case "invoice_approved":
          return "bg-green-100 text-green-800";

        // Statut jaune
        case "pending":
          return "bg-yellow-100 text-yellow-800";

        // Statut gris
        case "pending_additional_info":
          return "bg-gray-100 text-gray-800";

        // Statut par défaut
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
    quotationStatusClass: () => (status) => STATUS_CLASS_MAP[status] || DEFAULT_STATUS_CLASS,
    invoiceStatusClass: () => (status) => STATUS_CLASS_MAP[status] || DEFAULT_STATUS_CLASS,
    claimHandlers: () => () => {
      return ["merchant", "external_merchant", "estaly_partner"];
    },
    statuses: () => () => {
      return [
        "to_be_approved",
        "approved",
        "pending",
        "processing",
        "refused",
        "resolved",
        "cancelled",
        "pending_additional_info",
        "pending_deductible_payment",
        "pending_deductible_payment_and_final_settlement",
        "pending_final_settlement",
        "waiting_for_quotation",
        "quotation_to_be_approved",
        "quotation_approved",
        "quotation_refused",
        "waiting_for_invoice",
        "invoice_to_be_approved",
        "invoice_approved",
        "invoice_refused",
        "waiting_for_resolution",
        "waiting_for_customer_iban",
        "waiting_for_return_appointment",
        "pickup_scheduled",
      ];
    },
  },

  mutations: {
    setCurrentClaim(state, currentClaim) {
      state.currentClaim = currentClaim;
    },
    setIndex(state, data) {
      state.index = data.claims;
      state.currentPage = data.page.currentPage || 1;
      state.totalPages = data.page.totalPages || 0;
      state.totalCount = data.page.totalCount || 0;
    },
    setPendingCount(state, count) {
      state.pendingCount = count;
    },
  },

  actions: {
    async fetchIndex(
      { commit },
      { page, perPage, status = [], stores = [], keywords = [], claimHandler = [], includes = [] },
    ) {
      const response = await ClaimsService.fetchIndex({
        page,
        perPage,
        status,
        stores,
        keywords,
        claimHandler,
        includes,
      });

      if (!response?.success) return response;

      const data = toCamelCase(response.data);
      commit("setIndex", data);
      return response;
    },

    async fetchPendingCount({ commit }, { stores = [] } = {}) {
      const response = await ClaimsService.fetchIndex({
        page: 1,
        perPage: 1,
        status: ["waiting_for_quotation", "waiting_for_invoice"],
        claimHandler: ["merchant"],
        stores,
      });
      if (response?.success) {
        const data = toCamelCase(response.data);
        commit("setPendingCount", data.page?.totalCount ?? 0);
      }
      return response;
    },

    async fetch({ commit, dispatch }, claimFriendlyId) {
      const response = await ClaimsService.fetch(claimFriendlyId, [
        "store",
        "customer",
        "claim_declarations",
        "voucher",
        "claim_historical_datas",
        "plan_sale",
        "invoice",
        "quotation",
        "optional_data",
        "claim_product",
      ]);
      if (!response?.success) {
        dispatch("resetCurrentClaim");
        return response;
      }

      const claim = toCamelCase(response.data);
      commit("setCurrentClaim", claim);
      return response;
    },

    reset({ commit }) {
      commit("setCurrentClaim", null);
    },

    async uploadQuotation({ state, dispatch }, { costItems, file, productState }) {
      let formData = new FormData();
      formData.append("file", file);

      costItems
        .map((item) => toSnakeCase(item))
        .forEach((item, index) => {
          Object.keys(item).forEach((key) => {
            formData.append(`cost_items[${index}][${key}]`, item[key]);
          });
        });

      if (productState) {
        formData.append("product_state", productState);
      }

      const response = await ClaimsService.uploadQuotation(state.currentClaim.id, formData);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    declareIrreparable: async function ({ state, dispatch }, { message, reason, irreparabilityCertificate }) {
      let formData = new FormData();
      if (irreparabilityCertificate) formData.append("irreparability_certificate", irreparabilityCertificate);
      formData.append("message", message);
      formData.append("reason", reason);

      const response = await ClaimsService.declareIrreparable(state.currentClaim.id, formData);

      if (!response?.success) return response;

      return dispatch("fetch", state.currentClaim.friendlyId);
    },

    async extractCostItems({ state }, { file }) {
      if (!state.currentClaim?.id) {
        return { success: false, error: "missing_current_claim_id" };
      }
      return await ClaimsService.extractCostItems(state.currentClaim.id, file);
    },

    async uploadInvoice({ state, dispatch }, { costItems, file }) {
      let formData = new FormData();
      formData.append("file", file);

      costItems
        .map((item) => toSnakeCase(item))
        .forEach((item, index) => {
          Object.keys(item).forEach((key) => {
            formData.append(`cost_items[${index}][${key}]`, item[key]);
          });
        });

      const response = await ClaimsService.uploadInvoice(state.currentClaim.id, formData);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },
  },
};
